import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface SeriPortBilgisi {
  port: any; // SerialPort
  bilgi: any; // SerialPortInfo
  etiket: string;
}

export type SeriPortDurum = 'bagli-degil' | 'baglaniyor' | 'bagli' | 'hata';

@Injectable({ providedIn: 'root' })
export class SeriPortService {
  readonly destekleniyor = 'serial' in navigator;

  readonly durum$ = new BehaviorSubject<SeriPortDurum>('bagli-degil');
  readonly veriGeldi$ = new Subject<string>();
  readonly hataMesaji$ = new BehaviorSubject<string>('');

  private _port: any = null; // SerialPort
  private _reader: ReadableStreamDefaultReader<string> | null = null;
  private _readableStreamClosed: Promise<void> | null = null;
  private _okumaAktif = false;
  private _tampon = '';

  get aktifPort(): any { return this._port; }
  get bagli(): boolean { return this.durum$.value === 'bagli'; }

  /** Daha once izin verilmis portlari listele */
  async portlariListele(): Promise<SeriPortBilgisi[]> {
    if (!this.destekleniyor) return [];
    try {
      const portlar = await (navigator as any).serial.getPorts();
      return portlar.map((port: any, i: number) => {
        const bilgi = port.getInfo();
        return {
          port,
          bilgi,
          etiket: this.portEtiketi(bilgi, i),
        };
      });
    } catch (_) { return []; }
  }

  /** Kullanicidan yeni port sec (user gesture gerektirir) */
  async portIste(filtreler?: any[]): Promise<SeriPortBilgisi | null> {
    if (!this.destekleniyor) return null;
    try {
      const port = await (navigator as any).serial.requestPort({
        filters: filtreler || [],
      });
      const bilgi = port.getInfo();
      return { port, bilgi, etiket: this.portEtiketi(bilgi, 0) };
    } catch (_) {
      return null; // Kullanici iptal etti
    }
  }

  /** Porta baglan ve okumaya basla */
  async baglan(port: any, baudRate = 9600): Promise<boolean> {
    if (this._port) await this.kopar();

    this.durum$.next('baglaniyor');
    this.hataMesaji$.next('');

    try {
      // Port zaten aciksa (onceki session'dan kalmis olabilir) once kapat
      if (port.readable || port.writable) {
        try { await port.close(); } catch (_) {}
        // Kapanmasi icin kisa bekle
        await new Promise(r => setTimeout(r, 200));
      }
      await port.open({ baudRate, dataBits: 8, stopBits: 1, parity: 'none', bufferSize: 4096 });
      this._port = port;
      this.durum$.next('bagli');
      this.okuBasla();
      return true;
    } catch (e: any) {
      const msg = e?.message || 'Bağlantı hatası';
      // Baska uygulama kullaniyorsa kullaniciya bildir
      const ipucu = msg.toLowerCase().includes('failed to open')
        ? msg + ' — Port baska bir uygulama tarafindan kullaniliyor olabilir (Arduino IDE, PuTTY vb.). Diger uygulamalari kapatin.'
        : msg;
      this.hataMesaji$.next(ipucu);
      this.durum$.next('hata');
      return false;
    }
  }

  /** Baglantıyı kopar */
  async kopar(): Promise<void> {
    this._okumaAktif = false;

    // 1) Reader'i iptal et — bu pipeTo'nun da bitmesini saglar
    if (this._reader) {
      try {
        await this._reader.cancel();
      } catch (_) {}
      try {
        this._reader.releaseLock();
      } catch (_) {}
      this._reader = null;
    }

    // 2) pipeTo promise'inin bitmesini bekle (stream kilidini birakmasi icin)
    if (this._readableStreamClosed) {
      try {
        await this._readableStreamClosed;
      } catch (_) {}
      this._readableStreamClosed = null;
    }

    // 3) Port'u kapat
    if (this._port) {
      try {
        await this._port.close();
      } catch (_) {}
    }

    this._port = null;
    this._tampon = '';
    this.durum$.next('bagli-degil');
  }

  /** Porta veri yaz */
  async yaz(veri: string): Promise<boolean> {
    if (!this._port?.writable) return false;
    try {
      const encoder = new TextEncoder();
      const writer = this._port.writable.getWriter();
      await writer.write(encoder.encode(veri));
      writer.releaseLock();
      return true;
    } catch (_) { return false; }
  }

  /** Seri porttan surekli oku */
  private async okuBasla() {
    if (!this._port?.readable) return;
    this._okumaAktif = true;

    const decoder = new TextDecoderStream();
    this._readableStreamClosed = this._port.readable.pipeTo(decoder.writable);
    this._reader = decoder.readable.getReader();

    try {
      while (this._okumaAktif) {
        const { value, done } = await this._reader.read();
        if (done) break;
        if (value) this.veriIsle(value);
      }
    } catch (e: any) {
      if (this._okumaAktif) {
        this.hataMesaji$.next(e?.message || 'Okuma hatası');
        this.durum$.next('hata');
      }
    } finally {
      try { this._reader?.releaseLock(); } catch (_) {}
      this._reader = null;
      if (this._readableStreamClosed) {
        try { await this._readableStreamClosed; } catch (_) {}
        this._readableStreamClosed = null;
      }
    }
  }

  /** Gelen veriyi satir satir isle */
  private veriIsle(chunk: string) {
    this._tampon += chunk;
    const satirlar = this._tampon.split('\n');
    // Son parca henuz tamamlanmamis olabilir
    this._tampon = satirlar.pop() || '';

    for (const satir of satirlar) {
      const temiz = satir.trim();
      if (temiz.length > 0) {
        this.veriGeldi$.next(temiz);
      }
    }
  }

  /** Port etiketi olustur */
  private portEtiketi(bilgi: any, index: number): string {
    const vid = bilgi.usbVendorId;
    const pid = bilgi.usbProductId;
    if (vid && pid) {
      // NodeMCU / ESP8266 genellikle CP2102 veya CH340
      const chip = this.chipTani(vid, pid);
      return chip ? `${chip} (${vid.toString(16)}:${pid.toString(16)})` : `USB ${vid.toString(16)}:${pid.toString(16)}`;
    }
    return `Seri Port ${index + 1}`;
  }

  /** Bilinen USB-Serial chipleri tani */
  private chipTani(vid: number, pid: number): string {
    // CP210x (Silicon Labs) - NodeMCU v1
    if (vid === 0x10C4 && pid === 0xEA60) return 'CP2102 (NodeMCU)';
    // CH340 - NodeMCU v3 / klonlar
    if (vid === 0x1A86 && pid === 0x7523) return 'CH340 (NodeMCU)';
    // CH9102
    if (vid === 0x1A86 && pid === 0x55D4) return 'CH9102 (NodeMCU)';
    // FTDI FT232
    if (vid === 0x0403 && pid === 0x6001) return 'FT232 (FTDI)';
    // Arduino
    if (vid === 0x2341) return 'Arduino';
    // ESP32-S2/S3 native USB
    if (vid === 0x303A) return 'ESP32';
    return '';
  }
}
